<template>
  <ion-content fullscreen :scroll-y="false">
  <div class="notes-modal" :class="[direction, { 'recording-active': showRecordingPrompt }]">
    <div class="notes-fixed-header">
      <!-- Lesson header - shown if note is associated with a downloaded lesson -->
      <div v-if="showLessonHeader" class="lesson-header" @click="openLesson">
        <ion-icon v-if="lessonIconSrc" :src="lessonIconSrc" class="lesson-header-icon" />
        <ion-icon v-else :icon="documentTextOutline" class="lesson-header-icon" />
        <span class="lesson-header-title">{{ current.note_metadata.lessonTitle }}</span>
      </div>

      <sub-menu-bar :buttons="subMenuButtons" />

      <div class="title-section">
        <ion-item lines="none" class="title-item">
          <ion-input
            v-model="title"
            :placeholder="enterTitlePlaceholder"
            clear-input
            class="title-input"
          />
        </ion-item>
      </div>

      <div class="divider" />
    </div>

    <div class="notes-scroll-container" ref="scrollContainer">
      <div class="body-section">
        <ion-item lines="none" class="body-item">
          <ion-textarea
            ref="bodyTextarea"
            class="body-textarea"
            v-model="body"
            :auto-grow="true"
            :rows="14"
            :placeholder="enterTextPlaceholder"
          />
        </ion-item>
      </div>

      <div v-if="!isPwa" class="audio-list-section">
        <div
          v-for="clip in audioResources"
          :key="clip.Id"
          class="audio-card"
        >
          <div class="audio-card-content" @click="openClip(clip)">
            <ion-icon :icon="playCircleOutline" class="audio-card-icon" />
            <span class="audio-card-name">{{ clip.Name }}</span>
          </div>
          <ion-icon
            :icon="trash"
            class="audio-card-delete"
            @click.stop="deleteClip(clip)"
          />
        </div>
      </div>
    </div>

    <!-- Recording prompt modal - moved outside scroll container to cover entire screen -->
    <div v-if="showRecordingPrompt" class="recording-prompt-overlay" @click="handleOverlayClick">
      <div class="recording-prompt" @click.stop>
        <div class="recording-prompt-content">
          <img v-if="isRecording" src="/assets/img/audio.gif" alt="Recording" class="recording-gif" />
          
          <div v-if="!isRecording && recordingStopped" class="recording-save-section">
            <ion-input
              v-model="recordingTitle"
              :placeholder="enterTitlePlaceholder"
              class="recording-title-input"
            />
            <button 
              class="recording-save-btn" 
              @click="saveRecording"
              :aria-label="'Save recording'"
            >
              <ion-icon :icon="checkmark" class="recording-save-icon" />
            </button>
          </div>
          
          <button 
            v-if="!isRecording && !recordingStopped" 
            class="recording-cancel-btn" 
            @click="cancelRecording"
          >
            Cancel
          </button>
        </div>
        
        <button 
          v-if="isRecording" 
          class="recording-stop-btn" 
          @click="handleStopRecording"
          :aria-label="'Stop recording'"
        >
          <ion-icon :icon="stopCircleOutline" class="recording-stop-icon" />
        </button>
      </div>
    </div>

    <!-- Record button - always at bottom of page -->
    <div v-if="shouldShowRecordButton" class="record-button-fixed">
      <button class="record-button-center" @click="handleRecordClick" :aria-label="isRecording ? 'Stop recording' : 'Start recording'">
        <ion-icon :icon="micCircleOutline" class="record-button-icon" />
      </button>
    </div>
  </div>
</ion-content>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, onMounted, onUnmounted, nextTick, computed, watch } from 'vue';
import { IonContent, IonItem, IonInput, IonTextarea, IonIcon, IonToolbar, IonButtons, IonButton, modalController, alertController } from '@ionic/vue';
import { trash ,trashOutline, shareSocial, checkmark, micCircleOutline, stopCircleOutline, playCircleOutline, documentTextOutline } from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { Note, createNewNote, saveNote, deleteNote, toShareText, ensureTitle, AudioResourceMetadata } from '@/services/NotesService';
import { shareFile, logInfo, logError } from '@/utilities/Filesystem';
import { MimeTypes, ChipletButton } from '@/types/types';
import { EMPTY_GUID } from '@/constants/app';
import SubMenuBar from '@/components/SubMenuBar.vue';
import { useStore } from 'vuex';
import { AttachedResource, getMediaItem, saveMedia, Media, getJourneySet, deleteMedia } from '@/storage/DataStorage';
import { getImageDataUri } from '@/utilities/Compatibility';
import { v4 as uuidv4 } from 'uuid';
import { openResourceViewerModal } from '@/services/InformationPagesService';
import { Filesystem } from '@capacitor/filesystem';
import { CapacitorAudioRecorder } from '@capgo/capacitor-audio-recorder';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import translate from '@/views/translate';
export default defineComponent({
  name: 'NotesModal',
  components: { IonContent, IonItem, IonInput, IonTextarea, IonIcon, SubMenuBar },
  props: {
    dismiss: { type: Function as PropType<() => Promise<void>> },
    initialNote: { type: Object as PropType<Partial<Note>> }
  },
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const { t } = translate();
    const localeMessages = computed(() => store.getters.getLocaleMessages());
    const direction = computed(() => t("messages.language.textDirection"));
    const enterTitlePlaceholder = computed(() => localeMessages.value?.application?.enterTitlePlaceholder || 'Enter title');
    const enterTextPlaceholder = computed(() => localeMessages.value?.application?.enterTextPlaceholder || 'Start typing your note...');
    const audioNoteLabel = computed(() => localeMessages.value?.application?.audioNote || 'Audio Note');
    const current = ref<Note>(createNewNote(props.initialNote || {}));
    const title = ref<string>(current.value.note_metadata.note_title || '');
    const body = ref<string>(current.value.note_body || '');
    const audioResources = ref<AttachedResource[]>([]);
    const canRecord = ref(false);
    const isRecording = ref(false);
    const isPwa = ref(false);
    
    // Debug computed property to track record button visibility
    const shouldShowRecordButton = computed(() => {
      const show = !isPwa.value && canRecord.value;
      console.log('[NotesModal] shouldShowRecordButton computed:', {
        isPwa: isPwa.value,
        canRecord: canRecord.value,
        show: show
      });
      return show;
    });
    
    // Watch for changes to canRecord and isPwa
    watch(canRecord, (newVal: boolean, oldVal: boolean) => {
      console.log('[NotesModal] canRecord changed:', { oldVal, newVal, isPwa: isPwa.value });
    });
    
    watch(isPwa, (newVal: boolean, oldVal: boolean) => {
      console.log('[NotesModal] isPwa changed:', { oldVal, newVal, canRecord: canRecord.value });
    });
    
    watch(shouldShowRecordButton, (newVal: boolean, oldVal: boolean) => {
      console.log('[NotesModal] shouldShowRecordButton changed:', { oldVal, newVal });
    });
    const scrollContainer = ref<HTMLElement | null>(null);
    const bodyTextarea = ref<any>(null);
    const scrollHandler = ref<((e: Event) => void) | null>(null);
    const showRecordingPrompt = ref(false);
    const recordingStopped = ref(false);
    const recordingTitle = ref('');
    const recordingData = ref<{ uri: string; base64: string } | null>(null);
    const showLessonHeader = ref(false);
    const lessonIconSrc = ref<string>('');
    const isKeyboardVisible = ref(false);

    // Setup keyboard listeners early (separate from async mounted)
    onMounted(() => {
      // ensure initial auto-title for display only
      const ensured = ensureTitle({ ...current.value, note_metadata: { ...current.value.note_metadata, note_title: title.value }, note_body: body.value });
      title.value = ensured.note_metadata.note_title;
      Keyboard.setResizeMode({ mode: KeyboardResize.Body });

      Keyboard.addListener('keyboardWillShow', async (info) => {
        isKeyboardVisible.value = true;
        document.body.classList.add('keyboard-open');
        // Wait for next tick and a small delay to ensure keyboard animation starts
        await nextTick();
        setTimeout(() => {
          // Adjust scroll container to account for keyboard
          if (scrollContainer.value && info && info.keyboardHeight) {
            const container = scrollContainer.value;
            const rect = container.getBoundingClientRect();
            const availableHeight = window.innerHeight - rect.top - info.keyboardHeight;
            container.style.maxHeight = `${availableHeight}px`;
            container.style.paddingBottom = '12px';
          }
        }, 100);
      });

      Keyboard.addListener('keyboardWillHide', async () => {
        isKeyboardVisible.value = false;
        document.body.classList.remove('keyboard-open');
        // Wait for next tick to ensure DOM is updated
        await nextTick();
        // Reset scroll container height
        if (scrollContainer.value) {
          scrollContainer.value.style.maxHeight = '';
          scrollContainer.value.style.paddingBottom = '';
        }
        // Force viewport recalculation after keyboard closes
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      });

      // Blur textarea when scrolling the container to hide cursor
      scrollHandler.value = () => {
        if (bodyTextarea.value && bodyTextarea.value.$el) {
          const textareaEl = bodyTextarea.value.$el.querySelector('textarea');
          if (textareaEl && document.activeElement === textareaEl) {
            textareaEl.blur();
          }
        }
      };
      
      if (scrollContainer.value && scrollHandler.value) {
        scrollContainer.value.addEventListener('scroll', scrollHandler.value);
      }
    });

    const close = async () => {
      if (props.dismiss) {
        await props.dismiss();
      } else {
        await modalController.dismiss();
      }
    };

    const handleSave = async () => {
      const hasTitleOrText = (title.value || '').trim().length > 0 || (body.value || '').trim().length > 0;
      
      // Check if there are audio resources
      const audioResourcesList = current.value.note_metadata.audioResources || [];
      const legacyAudioIds = current.value.note_metadata.audioResourceIds || [];
      const hasAudioClips = audioResourcesList.length > 0 || legacyAudioIds.length > 0;
      
      // If no title, no text, and no audio clips, don't save
      if (!hasTitleOrText && !hasAudioClips) {
        await close();
        return;
      }
      
      // Determine the note title
      let noteTitle = title.value.trim();
      
      // If no title or text but has audio clips, generate title based on lesson association
      if (!hasTitleOrText && hasAudioClips) {
        const metadata = current.value.note_metadata;
        const isAssociatedWithLesson = metadata.lessonId && 
                                       metadata.lessonId !== EMPTY_GUID && 
                                       metadata.lessonSetId && 
                                       metadata.lessonSetId !== EMPTY_GUID &&
                                       metadata.lessonTitle;
        
        if (isAssociatedWithLesson) {
          // Use "Lesson Title - application_audioNote"
          noteTitle = `${metadata.lessonTitle} - ${audioNoteLabel.value}`;
        } else {
          // Use "application_audioNote + ordinal position"
          const ordinalPosition = audioResourcesList.length + legacyAudioIds.length;
          noteTitle = `${audioNoteLabel.value} ${ordinalPosition}`;
        }
      }
      
      const toPersist: Note = {
        ...current.value,
        note_metadata: { ...current.value.note_metadata, note_title: noteTitle },
        note_body: body.value,
      };
      const saved = await saveNote(toPersist);
      current.value = saved;
      await close();
    };

    const handleDelete = async () => {
      if (current.value.id && current.value.id !== EMPTY_GUID) {
        const messages = store.getters.getLocaleMessages();
        const alert = await alertController.create({
          cssClass: 'custom-alert',
          message: `<p>${messages.application.wishToContinue}</p>`,
          buttons: [
            {
              text: messages.application.no,
              cssClass: 'alert-button-no',
              role: 'cancel'
            },
            {
              text: messages.application.yes,
              cssClass: 'alert-button-yes',
              handler: async () => {
                await deleteNote(current.value.id);
                await close();
              }
            }
          ]
        });
        await alert.present();
      } else {
        await close();
      }
    };

    const handleShare = async () => {
      const hasContent = (title.value || '').trim().length > 0 || (body.value || '').trim().length > 0;
      if (!hasContent) return;
      const text = toShareText({ ...current.value, note_metadata: { ...current.value.note_metadata, note_title: title.value }, note_body: body.value });
      await shareFile('note.txt', text, true, MimeTypes.TEXT);
    };

    const checkLessonStatus = async () => {
      const metadata = current.value.note_metadata;
      // Check if note is associated with a lesson
      if (!metadata.lessonId || metadata.lessonId === EMPTY_GUID || 
          !metadata.lessonSetId || metadata.lessonSetId === EMPTY_GUID) {
        showLessonHeader.value = false;
        return;
      }

      try {
        // Check if journey set is downloaded
        const journeySet = await getJourneySet(metadata.lessonSetId);
        if (!journeySet || !journeySet.alreadyDownloaded) {
          showLessonHeader.value = false;
          return;
        }

        // Journey set is downloaded, show header
        showLessonHeader.value = true;

        // Load lesson icon if available
        if (metadata.lessonIconId && metadata.lessonIconId !== EMPTY_GUID) {
          try {
            const media = await getMediaItem(metadata.lessonIconId);
            lessonIconSrc.value = getImageDataUri(media.content);
          } catch {
            lessonIconSrc.value = '';
          }
        } else {
          lessonIconSrc.value = '';
        }
      } catch (e: any) {
        await logInfo('[NotesModal] checkLessonStatus failed', 1);
        await logError(e, '[NotesModal] checkLessonStatus exception');
        showLessonHeader.value = false;
      }
    };

    const openLesson = async () => {
      const metadata = current.value.note_metadata;
      if (!metadata.lessonId || metadata.lessonId === EMPTY_GUID || 
          !metadata.lessonSetId || metadata.lessonSetId === EMPTY_GUID) {
        return;
      }

      try {
        // Get journey set to find quadrantId (fieldId)
        const journeySet = await getJourneySet(metadata.lessonSetId);
        if (!journeySet || !journeySet.alreadyDownloaded) {
          return;
        }

        // Close all modals (NotesModal and NotesListModal if open)
        // First dismiss current modal (NotesModal)
        if (props.dismiss) {
          await props.dismiss();
        } else {
          await modalController.dismiss();
        }
        // Then dismiss parent modal (NotesListModal) if it exists
        await nextTick();
        try {
          await modalController.dismiss();
        } catch {
          // Parent modal might not exist, ignore
        }

        // Navigate to the lesson
        await router.push({
          name: 'journey',
          params: {
            fieldId: journeySet.quadrantId,
            journeySetId: metadata.lessonSetId,
            journeyId: metadata.lessonId
          }
        });
      } catch (e: any) {
        await logInfo('[NotesModal] openLesson failed', 1);
        await logError(e, '[NotesModal] openLesson exception');
      }
    };

    const loadAudioResources = async () => {
      await logInfo('[NotesModal] loadAudioResources start', 2);
      audioResources.value = [];
      
      // Use new audioResources structure if available, otherwise fall back to legacy audioResourceIds
      const resources = current.value.note_metadata.audioResources || [];
      const legacyIds = current.value.note_metadata.audioResourceIds || [];
      
      await logInfo(`[NotesModal] audio resources: ${JSON.stringify(resources)}`, 2);
      
      // Load new format
      for (const resource of resources) {
        try {
          const media = await getMediaItem(resource.id);
          // Default to audio/mpeg data URI
          const dataUri = media.content.startsWith('data:') ? media.content : `data:audio/mpeg;base64,${media.content}`;
          audioResources.value.push({
            Id: resource.id,
            Name: resource.title,
            ResourceData: dataUri,
            MimeType: 'audio/mpeg',
            Shareable: true
          } as AttachedResource);
          await logInfo(`[NotesModal] loaded audio media id=${resource.id}`, 2);
        } catch {
          // skip missing media
          await logInfo(`[NotesModal] missing audio media id=${resource.id}`, 1);
        }
      }
      
      // Load legacy format (without titles)
      for (const id of legacyIds) {
        // Skip if already loaded from new format
        if (resources.some(r => r.id === id)) continue;
        try {
          const media = await getMediaItem(id);
          const dataUri = media.content.startsWith('data:') ? media.content : `data:audio/mpeg;base64,${media.content}`;
          audioResources.value.push({
            Id: id,
            Name: 'Audio snippet',
            ResourceData: dataUri,
            MimeType: 'audio/mpeg',
            Shareable: true
          } as AttachedResource);
          await logInfo(`[NotesModal] loaded legacy audio media id=${id}`, 2);
        } catch {
          await logInfo(`[NotesModal] missing legacy audio media id=${id}`, 1);
        }
      }
      
      await logInfo(`[NotesModal] loadAudioResources done, count=${audioResources.value.length}`, 2);
    };

    const ensurePermission = async (): Promise<boolean> => {
      try {
        await logInfo('[NotesModal] ensurePermission: checking mic permission', 2);
        const perm = await CapacitorAudioRecorder.checkPermissions();
        if (perm.recordAudio === 'granted') {
          await logInfo('[NotesModal] ensurePermission: already granted', 2);
          return true;
        }
        await logInfo('[NotesModal] ensurePermission: requesting permission', 2);
        const req = await CapacitorAudioRecorder.requestPermissions();
        const granted = req.recordAudio === 'granted';
        await logInfo(`[NotesModal] ensurePermission: request result=${granted}`, 2);
        return granted;
      } catch (e: any) {
        await logInfo('[NotesModal] ensurePermission: exception', 1);
        await logError(e, '[NotesModal] ensurePermission: exception');
        return false;
      }
    };

    const startRecording = async () => {
      if (!(await ensurePermission())) return;
      try {
        await logInfo('[NotesModal] startRecording', 2);
        await CapacitorAudioRecorder.startRecording({
              sampleRate: 44100,
              bitRate: 64000  // 64 kbps
            });
        isRecording.value = true;
        recordingStopped.value = false;
        recordingTitle.value = '';
        recordingData.value = null;
        showRecordingPrompt.value = true;
        await nextTick(); // Force DOM update
        await logInfo(`[NotesModal] startRecording success, isRecording=${isRecording.value}`, 2);
      } catch (e: any) {
        await logInfo('[NotesModal] startRecording failed', 1);
        await logError(e, '[NotesModal] startRecording exception');
        isRecording.value = false;
        showRecordingPrompt.value = false;
        await nextTick(); // Force DOM update
      }
    };

    const dismissKeyboard = async () => {
      try {
        await Keyboard.hide();
        await logInfo('[NotesModal] Keyboard dismissed', 2);
      } catch (e: any) {
        // Keyboard plugin might not be available on all platforms
        await logInfo('[NotesModal] Keyboard dismiss not available', 2);
      }
    };

    const toggleKeyboard = async () => {
      try {
        // Use the tracked keyboard visibility state as primary indicator
        // Also check if any input/textarea is currently focused as fallback
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement instanceof HTMLElement && (
            activeElement.closest('ion-input') !== null ||
            activeElement.closest('ion-textarea') !== null
          ))
        );

        // If keyboard is visible OR an input is focused, hide it
        if (isKeyboardVisible.value || isInputFocused) {
          // If an input is focused, hide keyboard
          // First blur the active element
          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }
          
          // Also blur any focused inputs/textareas in the modal
          const focusedInputs = document.querySelectorAll('.notes-modal input:focus, .notes-modal textarea:focus');
          focusedInputs.forEach((el) => {
            if (el instanceof HTMLElement) {
              el.blur();
            }
          });
          
          // Wait a moment for blur to complete, then hide the keyboard
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 50));
          await Keyboard.hide();
          // Update state immediately since we're hiding it manually
          isKeyboardVisible.value = false;
          await logInfo('[NotesModal] Keyboard hidden', 2);
        } else {
          // If no input is focused, show keyboard by focusing body textarea
          // First, blur any active element to ensure clean state
          if (activeElement && activeElement instanceof HTMLElement) {
            activeElement.blur();
          }
          
          await nextTick(); // Ensure DOM is updated
          
          // Try multiple ways to access the textarea element
          let textareaEl: HTMLTextAreaElement | null = null;
          
          // Method 1: Through Vue ref
          if (bodyTextarea.value) {
            if (bodyTextarea.value.$el) {
              textareaEl = bodyTextarea.value.$el.querySelector('textarea');
            } else if (bodyTextarea.value.querySelector) {
              // Direct element access
              textareaEl = bodyTextarea.value.querySelector('textarea');
            }
          }
          
          // Method 2: Direct DOM query (fallback)
          if (!textareaEl) {
            // Try finding through the body-section container
            const bodySection = document.querySelector('.body-section .body-textarea');
            if (bodySection) {
              textareaEl = bodySection.querySelector('textarea');
            }
            // If still not found, try direct query
            if (!textareaEl) {
              const allTextareas = document.querySelectorAll('.notes-modal textarea');
              // Find the one in the body section (should be the first/last one)
              for (const ta of Array.from(allTextareas)) {
                if (ta.closest('.body-section')) {
                  textareaEl = ta as HTMLTextAreaElement;
                  break;
                }
              }
            }
          }
          
          if (textareaEl) {
            // Scroll textarea into view first
            textareaEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Use click event to trigger focus (more reliable on mobile)
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            textareaEl.dispatchEvent(clickEvent);
            
            // Then focus the textarea
            textareaEl.focus();
            // Set cursor to beginning of first line
            textareaEl.setSelectionRange(0, 0);
            
            // Force focus again with a small delay to ensure keyboard shows on mobile
            await nextTick();
            setTimeout(() => {
              textareaEl?.focus();
              textareaEl?.setSelectionRange(0, 0);
            }, 100);
            
            await logInfo('[NotesModal] Keyboard shown, cursor at start', 2);
          } else {
            await logInfo('[NotesModal] toggleKeyboard: textarea element not found', 1);
          }
        }
      } catch (e: any) {
        await logInfo('[NotesModal] toggleKeyboard failed', 1);
        await logError(e, '[NotesModal] toggleKeyboard exception');
      }
    };

    const cancelRecording = async () => {
      if (isRecording.value) {
        try {
          await CapacitorAudioRecorder.stopRecording();
        } catch {
          // ignore errors when canceling
        }
      }
      showRecordingPrompt.value = false;
      isRecording.value = false;
      recordingStopped.value = false;
      recordingData.value = null;
      recordingTitle.value = '';
    };

    const handleStopRecording = async () => {
      try {
        await logInfo('[NotesModal] handleStopRecording', 2);
        const result = await CapacitorAudioRecorder.stopRecording();
        isRecording.value = false;
        recordingStopped.value = true;
        await nextTick(); // Force DOM update
        
        await logInfo(`[NotesModal] stopRecording result=${JSON.stringify(result)}`, 2);
        
        // The plugin returns 'uri' not 'filePath'
        const fileUri = result?.uri;
        await logInfo(`[NotesModal] stopRecording uri=${fileUri}`, 2);
        
        if (!fileUri) {
          await logInfo('[NotesModal] stopRecording: no uri returned', 1);
          cancelRecording();
          return;
        }
        
        const file = await Filesystem.readFile({ path: fileUri });
        const base64 = typeof file.data === 'string' ? file.data : '';
        await logInfo(`[NotesModal] readFile success size=${base64?.length || 0}`, 2);
        if (!base64) {
          await logInfo('[NotesModal] stopRecording: no base64 data', 1);
          cancelRecording();
          return;
        }
        
        // Store recording data for saving
        recordingData.value = { uri: fileUri, base64 };
      } catch (e: any) {
        await logInfo('[NotesModal] handleStopRecording failed', 1);
        await logError(e, '[NotesModal] handleStopRecording exception');
        isRecording.value = false;
        recordingStopped.value = false;
        cancelRecording();
        await nextTick(); // Force DOM update
      }
    };

    const saveRecordingWithDefaultTitle = async () => {
      if (!recordingData.value) return;
      
      try {
        // Get existing audio resources to determine ordinal position
        const existingResources = current.value.note_metadata.audioResources || [];
        const ordinalPosition = existingResources.length + 1;
        
        // Use default title: audioNoteLabel + ordinal position
        const clipTitle = `${audioNoteLabel.value} ${ordinalPosition}`;
        await logInfo(`[NotesModal] Saving audio clip with default title: ${clipTitle}`, 2);
        
        // Save to media and attach to note
        const mediaId = uuidv4();
        const content = recordingData.value.base64; // store raw base64
        await saveMedia({ id: mediaId, content } as Media);
        await logInfo(`[NotesModal] saveMedia id=${mediaId}`, 2);
        
        // Use new audioResources structure
        const resources = current.value.note_metadata.audioResources || [];
        resources.push({ id: mediaId, title: clipTitle } as AudioResourceMetadata);
        current.value.note_metadata.audioResources = resources;
        
        // Save the note with updated audio resources
        const savedNote = await saveNote({ 
          ...current.value, 
          note_metadata: { 
            ...current.value.note_metadata, 
            note_title: title.value 
          }, 
          note_body: body.value 
        });
        current.value = savedNote;
        await logInfo('[NotesModal] saveNote after audio attach', 2);
        
        // Close prompt and reload list
        showRecordingPrompt.value = false;
        recordingStopped.value = false;
        recordingData.value = null;
        recordingTitle.value = '';
        await loadAudioResources();
      } catch (e: any) {
        await logInfo('[NotesModal] saveRecordingWithDefaultTitle failed', 1);
        await logError(e, '[NotesModal] saveRecordingWithDefaultTitle exception');
      }
    };

    const handleOverlayClick = async (event: MouseEvent) => {
      // Only handle clicks directly on the overlay, not on the prompt itself
      if (event.target === event.currentTarget) {
        if (isRecording.value) {
          // If recording, cancel and don't save
          await cancelRecording();
        } else if (recordingStopped.value && recordingData.value) {
          // If stopped recording, save with default title
          await saveRecordingWithDefaultTitle();
        }
      }
    };

    const saveRecording = async () => {
      if (!recordingData.value) return;
      
      try {
        // Get existing audio resources to determine ordinal position
        const existingResources = current.value.note_metadata.audioResources || [];
        const ordinalPosition = existingResources.length + 1;
        
        // Use entered title, or fall back to audioNoteLabel + ordinal position
        const clipTitle = recordingTitle.value.trim() || `${audioNoteLabel.value} ${ordinalPosition}`;
        await logInfo(`[NotesModal] Saving audio clip with title: ${clipTitle}`, 2);
        
        // Save to media and attach to note
        const mediaId = uuidv4();
        const content = recordingData.value.base64; // store raw base64
        await saveMedia({ id: mediaId, content } as Media);
        await logInfo(`[NotesModal] saveMedia id=${mediaId}`, 2);
        
        // Use new audioResources structure
        const resources = current.value.note_metadata.audioResources || [];
        resources.push({ id: mediaId, title: clipTitle } as AudioResourceMetadata);
        current.value.note_metadata.audioResources = resources;
        
        // Save the note with updated audio resources
        const savedNote = await saveNote({ 
          ...current.value, 
          note_metadata: { 
            ...current.value.note_metadata, 
            note_title: title.value 
          }, 
          note_body: body.value 
        });
        current.value = savedNote;
        await logInfo('[NotesModal] saveNote after audio attach', 2);
        
        // Close prompt and reload list
        showRecordingPrompt.value = false;
        recordingStopped.value = false;
        recordingData.value = null;
        recordingTitle.value = '';
        await loadAudioResources();
      } catch (e: any) {
        await logInfo('[NotesModal] saveRecording failed', 1);
        await logError(e, '[NotesModal] saveRecording exception');
      }
    };

    const handleRecordClick = async () => {
      await logInfo(`[NotesModal] handleRecordClick START, isRecording=${isRecording.value}`, 2);
      if (!isRecording.value) {
        await startRecording();
      }
      // Force Vue to update the DOM
      await nextTick();
      await logInfo(`[NotesModal] handleRecordClick END, isRecording=${isRecording.value}`, 2);
    };

    const deleteClip = async (clip: AttachedResource) => {
      const messages = store.getters.getLocaleMessages();
      const alert = await alertController.create({
        cssClass: 'custom-alert',
        message: `<p>${messages.application.wishToContinue}</p>`,
        buttons: [
          {
            text: messages.application.no,
            cssClass: 'alert-button-no',
            role: 'cancel'
          },
          {
            text: messages.application.yes,
            cssClass: 'alert-button-yes',
            handler: async () => {
              try {
                await logInfo(`[NotesModal] deleteClip id=${clip.Id}`, 2);
                
                // Delete the media item from storage
                try {
                  await deleteMedia(clip.Id);
                  await logInfo(`[NotesModal] deleteMedia id=${clip.Id}`, 2);
                } catch (e: any) {
                  await logInfo(`[NotesModal] deleteMedia failed for id=${clip.Id}`, 1);
                  await logError(e, '[NotesModal] deleteMedia exception');
                }
                
                // Remove from audioResources array
                const resources = current.value.note_metadata.audioResources || [];
                const updatedResources = resources.filter(r => r.id !== clip.Id);
                current.value.note_metadata.audioResources = updatedResources;
                
                // Also remove from legacy audioResourceIds if present
                const legacyIds = current.value.note_metadata.audioResourceIds || [];
                const updatedLegacyIds = legacyIds.filter(id => id !== clip.Id);
                current.value.note_metadata.audioResourceIds = updatedLegacyIds;
                
                // Save the note with updated resources
                const savedNote = await saveNote({ 
                  ...current.value, 
                  note_metadata: { 
                    ...current.value.note_metadata, 
                    note_title: title.value 
                  }, 
                  note_body: body.value 
                });
                current.value = savedNote;
                await logInfo('[NotesModal] saveNote after audio delete', 2);
                
                // Reload the list
                await loadAudioResources();
              } catch (e: any) {
                await logInfo('[NotesModal] deleteClip failed', 1);
                await logError(e, '[NotesModal] deleteClip exception');
              }
            }
          }
        ]
      });
      await alert.present();
    };

    const openClip = async (res: AttachedResource) => {
      await openResourceViewerModal(res);
    };

    const saveRecordingIfActive = async () => {
      if (!isRecording.value) return;
      
      try {
        await logInfo('[NotesModal] Auto-saving recording on modal close', 2);
        const result = await CapacitorAudioRecorder.stopRecording();
        isRecording.value = false;
        
        await logInfo(`[NotesModal] Auto-save recording result=${JSON.stringify(result)}`, 2);
        
        const fileUri = result?.uri;
        if (!fileUri) {
          await logInfo('[NotesModal] Auto-save: no uri returned', 1);
          return;
        }
        
        const file = await Filesystem.readFile({ path: fileUri });
        const base64 = typeof file.data === 'string' ? file.data : '';
        if (!base64) {
          await logInfo('[NotesModal] Auto-save: no base64 data', 1);
          return;
        }
        
        // Get existing audio resources to determine ordinal position
        const existingResources = current.value.note_metadata.audioResources || [];
        const ordinalPosition = existingResources.length + 1;
        
        // Use audioNoteLabel + ordinal position as default title
        const defaultTitle = `${audioNoteLabel.value} ${ordinalPosition}`;
        await logInfo(`[NotesModal] Auto-saving audio clip with default title: ${defaultTitle}`, 2);
        
        const mediaId = uuidv4();
        await saveMedia({ id: mediaId, content: base64 } as Media);
        await logInfo(`[NotesModal] Auto-save saveMedia id=${mediaId}`, 2);
        
        // Use new audioResources structure
        const resources = current.value.note_metadata.audioResources || [];
        resources.push({ id: mediaId, title: defaultTitle } as AudioResourceMetadata);
        current.value.note_metadata.audioResources = resources;
        
        // Save the note with updated audio resources
        await saveNote({ 
          ...current.value, 
          note_metadata: { 
            ...current.value.note_metadata, 
            note_title: title.value 
          }, 
          note_body: body.value 
        });
        await logInfo('[NotesModal] Auto-save saveNote complete', 2);
      } catch (e: any) {
        await logInfo('[NotesModal] Auto-save recording failed', 1);
        await logError(e, '[NotesModal] Auto-save exception');
      }
    };

    onMounted(async () => {
      // compute canRecord based on preferences and platform
      try {
        console.log('[NotesModal] onMounted: Starting initialization');
        const preferences = store.getters.getPreferences();
        console.log('[NotesModal] onMounted: preferences =', preferences);
        console.log('[NotesModal] onMounted: preferences.allowAudioInnotes =', preferences?.allowAudioInnotes);
        console.log('[NotesModal] onMounted: typeof preferences.allowAudioInnotes =', typeof preferences?.allowAudioInnotes);
        
        const allowed = preferences.allowAudioInnotes;
        console.log('[NotesModal] onMounted: allowed =', allowed);
        
        isPwa.value = store.getters.isPwa();
        console.log('[NotesModal] onMounted: isPwa.value =', isPwa.value);
        console.log('[NotesModal] onMounted: store.getters.isPwa() =', store.getters.isPwa());
        
        canRecord.value = allowed && !isPwa.value;
        console.log('[NotesModal] onMounted: canRecord.value =', canRecord.value);
        console.log('[NotesModal] onMounted: allowed && !isPwa.value =', allowed && !isPwa.value);
        
        await logInfo(`[NotesModal] mounted allowAudioInnotes=${allowed} isPwa=${isPwa.value} canRecord=${canRecord.value}`, 2);
      } catch (e) {
        console.error('[NotesModal] onMounted: Error during initialization', e);
        canRecord.value = false;
      }
      await checkLessonStatus();
      await loadAudioResources();
    });

    onUnmounted(async () => {
      await saveRecordingIfActive();
      Keyboard.removeAllListeners();
      
      // Remove scroll listener
      if (scrollContainer.value && scrollHandler.value) {
        scrollContainer.value.removeEventListener('scroll', scrollHandler.value);
      }
    });

    const subMenuButtons = computed(() => {
      const buttons: ChipletButton[] = [
        {
          src: '/assets/img/keyboard.svg',
          visibilityClass: '',
          handler: async () => { await toggleKeyboard(); }
        },  
        {
          icon: trash,
          visibilityClass: '',
          handler: async () => { await handleDelete(); }
        }
      ];
      
      // Only show share button if shareUnencryptedFunctionality is enabled
      if (localeMessages.value?.buildConfigs?.config?.shareUnencryptedFunctionality === 'true') {
        buttons.push({
          icon: shareSocial,
          visibilityClass: '',
          handler: async () => { await handleShare(); }
        });
      }
      
      buttons.push({
        icon: checkmark,
        visibilityClass: '',
        handler: async () => { await handleSave(); }
      });
      
      return buttons;
    });

    return { 
      title, 
      body, 
      handleSave, 
      handleDelete, 
      handleShare, 
      trash, 
      shareSocial, 
      checkmark, 
      micCircleOutline, 
      stopCircleOutline, 
      playCircleOutline,
      trashOutline,
      documentTextOutline,
      subMenuButtons, 
      canRecord, 
      isRecording, 
      handleRecordClick,
      dismissKeyboard,
      audioResources, 
      openClip,
      deleteClip,
      isPwa,
      scrollContainer,
      showRecordingPrompt,
      recordingStopped,
      recordingTitle,
      handleStopRecording,
      saveRecording,
      saveRecordingWithDefaultTitle,
      cancelRecording,
      handleOverlayClick,
      current,
      showLessonHeader,
      lessonIconSrc,
      openLesson,
      enterTitlePlaceholder,
      enterTextPlaceholder,
      audioNoteLabel,
      direction,
      shouldShowRecordButton
    };
  }
});
</script>

<style scoped>
.notes-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.notes-fixed-header {
  flex-shrink: 0;
  padding: 8px 12px 0 12px;
  z-index: 1;
}

.lesson-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.lesson-header:hover {
  background-color: var(--ion-color-light);
}

.lesson-header-icon {
  font-size: 24px;
  width: 24px;
  height: 24px;
  color: rgb(100, 100, 100);
  flex-shrink: 0;
}

.lesson-header-title {
  font-size: 16px;
  color: rgb(100, 100, 100);
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 0 12px 12px 12px;
  padding-bottom: 84px; /* Add padding for record button (72px height + 12px margin) */
  min-height: 0;
  position: relative;
}

.title-section {
  background: var(--color-card-background);
  border-radius: 12px;
  margin: 0 0 6px 0;
}

.title-item {
  --ion-background-color: transparent;
  --padding-start: 12px;
  --inner-padding-start: 5px;
}

.title-input {
  --font-weight: bold !important;
  font-weight: bold !important;
}

.title-input input {
  font-weight: bold !important;
}

.title-input::part(native) {
  font-weight: bold !important;
}

.divider {
  height: 1px;
  background: var(--color-light-grey);
  margin: 4px 0 6px 0;
}

.body-section {
  position: relative;
  background: var(--color-card-background);
  border-radius: 12px;
  padding: 6px 6px 2px 6px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.body-item {
  --ion-background-color: transparent;
  --padding-start: 12px;
  --inner-padding-start: 0px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.body-textarea {
  width: 100%;
  min-height: 200px;
  max-height: 400px;
  flex: 1;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.body-textarea textarea {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  resize: none !important;
}

.body-textarea::part(native) {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* Record button - fixed at bottom of page */
.record-button-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  background: var(--color-primary);
  z-index: 10;
}

.record-button-center {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.record-button-icon {
  font-size: 48px;
  color: var(--color-primary-contrast);
  transition: transform 0.1s ease;
}

.record-button-center:active .record-button-icon {
  transform: scale(0.95);
}
.audio-list-section {
  margin-top: 10px;
  background: var(--color-card-background);
  border-radius: 12px;
  padding: 8px;
}
.audio-header {
  margin: 4px 0 6px 0;
  font-size: 14px;
  color: var(--ion-color-medium);
}
.audio-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--ion-color-light);
  color: var(--ion-color-dark);
  border-radius: 12px;
  padding: 6px 10px;
  margin: 4px 6px 0 0;
  cursor: pointer;
}
.audio-chip-icon {
  font-size: 16px;
}

.audio-list-section {
  margin-top: 1rem;
  padding: 0 12px;
}

.audio-header {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--ion-color-step-600, #444);
}

.audio-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-card-background);
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 10px;
  color:rgb(100,100,100);
  cursor: pointer;
  box-shadow: 0px 1px 3px  var(--color-primary);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.audio-card-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  cursor: pointer;
}

.audio-card-icon {
  font-size: 24px;
  color: var(--color-primary);
}

.audio-card-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
}

.audio-card-delete {
  font-size: 22px;
  color:rgb(100,100,100);
  cursor: pointer;
  margin-left: 10px;
}

/* Recording prompt overlay - covers entire screen including fixed header */
.recording-prompt-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  pointer-events: auto;
}

/* Disable interactions when recording prompt is active */
.notes-modal.recording-active .notes-fixed-header,
.notes-modal.recording-active .notes-fixed-header *,
.notes-modal.recording-active .notes-scroll-container,
.notes-modal.recording-active .notes-scroll-container *,
.notes-modal.recording-active .record-button-fixed,
.notes-modal.recording-active .record-button-fixed *,
.notes-modal.recording-active .title-input,
.notes-modal.recording-active .title-input input,
.notes-modal.recording-active .title-input ion-input,
.notes-modal.recording-active .body-textarea,
.notes-modal.recording-active .body-textarea textarea,
.notes-modal.recording-active .body-textarea ion-textarea,
.notes-modal.recording-active .sub-menu-bar,
.notes-modal.recording-active .sub-menu-bar *,
.notes-modal.recording-active .sub-menu-bar button,
.notes-modal.recording-active .sub-menu-bar ion-icon {
  pointer-events: none !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  touch-action: none !important;
}

.recording-prompt {
  background: var(--color-card-background);
  border-radius: 16px;
  padding: 0;
  max-width: 90%;
  width: 320px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.recording-prompt-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1;
  padding: 24px;
}

.recording-stop-btn {
  background: var(--color-primary);
  border: none;
  border-radius: 0 0 16px 16px;
  width: 100%;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s ease;
  margin: 0;
}

.recording-stop-btn:active {
  transform: scale(0.98);
}

.recording-stop-icon {
  font-size: 48px;
  color: var(--color-primary-contrast);
}

.recording-gif {
  width: 120px;
  height: auto;
  max-height: 120px;
}

.recording-save-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.recording-title-input {
  width: 100%;
  background: var(--ion-color-light);
  border-radius: 8px;
  padding: 8px 12px;
}

.recording-save-btn {
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s ease;
}

.recording-save-btn:active {
  transform: scale(0.95);
}

.recording-save-icon {
  font-size: 24px;
  color: white;
}

.recording-cancel-btn {
  background: transparent;
  color: var(--ion-color-medium);
  border: 1px solid var(--ion-color-medium);
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
}

/* RTL Support */
.rtl .lesson-header {
  flex-direction: row-reverse;
}

.rtl .lesson-header-title {
  text-align: right;
}

.rtl .title-input input {
  text-align: right;
}

.rtl .body-textarea textarea {
  text-align: right;
  direction: rtl;
}

.rtl .audio-card-content {
  flex-direction: row-reverse;
}

.rtl .audio-card-delete {
  margin-left: 0;
  margin-right: 10px;
}

.rtl .recording-save-section {
  direction: rtl;
}

.rtl .recording-title-input {
  text-align: right;
  direction: rtl;
}
</style>


